import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  fetchShortComments,
  fetchShorts,
  postShortComment,
  recordShortShare,
  toggleShortLike,
} from '../../api';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';
import { resolveImageUrl } from '../../utils/imageUrl';
import './ShortsRail.css';

const Icon = ({ name }) => {
  const paths = {
    like: <path d="M7 10v12H3V10h4Zm4 12H8V10l5-8 2 1v6h5a2 2 0 0 1 2 2l-2 9a2 2 0 0 1-2 2h-7Z" />,
    comment: <path d="M21 12a8 8 0 0 1-8 8H6l-4 3V12a9 9 0 1 1 19 0Z" />,
    share: <path d="m14 4 7 7-7 7v-4C8 14 5 16 3 20c0-8 4-12 11-12V4Z" />,
    copy: <><rect x="8" y="8" width="13" height="13" rx="2" /><path d="M16 8V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h3" /></>,
  };
  return <svg viewBox="0 0 24 24" aria-hidden="true" fill={name === 'like' ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">{paths[name]}</svg>;
};

const countLabel = (value) => {
  const count = Number(value) || 0;
  return count > 999 ? `${(count / 1000).toFixed(count > 9999 ? 0 : 1)}K` : String(count);
};

const ShortsRail = ({ shorts: suppliedShorts }) => {
  const [shorts, setShorts] = useState(suppliedShorts || []);
  const [loading, setLoading] = useState(!suppliedShorts);
  const [activeShort, setActiveShort] = useState(null);
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [comments, setComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [commentSaving, setCommentSaving] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const touchStartY = useRef(null);
  const wheelLocked = useRef(false);
  const { showSuccess, showError } = useToast();
  const { isAuthenticated, loading: authLoading, user } = useAuth();

  const updateShort = (id, changes) => {
    setShorts((items) => items.map((item) => item.id === id ? { ...item, ...changes } : item));
    setActiveShort((item) => item?.id === id ? { ...item, ...changes } : item);
  };

  const navigateShort = useCallback((direction) => {
    if (shorts.length < 2) return;
    setActiveShort((current) => {
      const currentIndex = shorts.findIndex((short) => short.id === current?.id);
      const nextIndex = (currentIndex + direction + shorts.length) % shorts.length;
      return shorts[nextIndex];
    });
    setComments([]);
    setCommentsOpen(false);
    setShareOpen(false);
  }, [shorts]);

  useEffect(() => {
    if (suppliedShorts) {
      setShorts(suppliedShorts);
      setLoading(false);
      return undefined;
    }
    let mounted = true;
    fetchShorts()
      .then((items) => { if (mounted) setShorts(items); })
      .catch((error) => console.error('Unable to load Shorts', error))
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [suppliedShorts]);

  useEffect(() => {
    if (loading || activeShort) return;
    const requestedId = Number(new URLSearchParams(window.location.search).get('short'));
    if (requestedId) setActiveShort(shorts.find((item) => item.id === requestedId) || null);
  }, [activeShort, loading, shorts]);

  useEffect(() => {
    if (!activeShort) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setActiveShort(null);
      if (event.key === 'ArrowUp') navigateShort(-1);
      if (event.key === 'ArrowDown') navigateShort(1);
    };
    document.addEventListener('keydown', onKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = '';
    };
  }, [activeShort, navigateShort]);

  const handleViewerWheel = (event) => {
    if (event.target.closest('.short-comments-panel, .short-share-panel') || Math.abs(event.deltaY) < 35 || wheelLocked.current) return;
    event.preventDefault();
    wheelLocked.current = true;
    navigateShort(event.deltaY > 0 ? 1 : -1);
    window.setTimeout(() => { wheelLocked.current = false; }, 500);
  };

  const handleTouchStart = (event) => {
    if (event.target.closest('.short-comments-panel, .short-share-panel')) {
      touchStartY.current = null;
      return;
    }
    touchStartY.current = event.touches[0]?.clientY ?? null;
  };

  const handleTouchEnd = (event) => {
    if (touchStartY.current === null) return;
    const endY = event.changedTouches[0]?.clientY ?? touchStartY.current;
    const distance = touchStartY.current - endY;
    touchStartY.current = null;
    if (Math.abs(distance) >= 55) navigateShort(distance > 0 ? 1 : -1);
  };

  useEffect(() => {
    const section = document.getElementById('shorts');
    if (!section) return;
    section.hidden = !loading && shorts.length === 0;
    return () => { section.hidden = false; };
  }, [loading, shorts.length]);

  const openComments = async () => {
    setCommentsOpen(true);
    setShareOpen(false);
    if (commentsLoading || comments.length) return;
    setCommentsLoading(true);
    try {
      setComments(await fetchShortComments(activeShort.id));
    } catch (error) {
      showError(error.message);
    } finally {
      setCommentsLoading(false);
    }
  };

  const handleLike = async () => {
    if (actionBusy) return;
    setActionBusy(true);
    try {
      const result = await toggleShortLike(activeShort.id);
      updateShort(activeShort.id, { liked: result.liked, likeCount: result.likeCount });
    } catch (error) {
      showError(error.message);
    } finally {
      setActionBusy(false);
    }
  };

  const submitComment = async (event) => {
    event.preventDefault();
    if (commentSaving) return;
    setCommentSaving(true);
    try {
      const result = await postShortComment(activeShort.id, { content: commentText });
      setComments((items) => [result.comment, ...items]);
      setCommentText('');
      updateShort(activeShort.id, { commentCount: result.commentCount });
      showSuccess(result.message);
    } catch (error) {
      showError(error.message);
    } finally {
      setCommentSaving(false);
    }
  };

  const shareUrl = () => `${window.location.origin}${window.location.pathname}?short=${activeShort.id}#shorts`;
  const trackShare = async (channel) => {
    try {
      const result = await recordShortShare(activeShort.id, channel);
      updateShort(activeShort.id, { shareCount: result.shareCount });
    } catch (error) {
      console.error('Unable to record Short share', error);
    }
  };

  const openShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: activeShort.title, text: activeShort.description || activeShort.title, url: shareUrl() });
        await trackShare('native');
        return;
      } catch (error) {
        if (error.name === 'AbortError') return;
      }
    }
    setCommentsOpen(false);
    setShareOpen(true);
  };

  const shareTo = async (channel) => {
    const url = shareUrl();
    if (channel === 'copy') {
      await navigator.clipboard.writeText(url);
      showSuccess('Short link copied.');
    } else {
      const destinations = {
        facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
        x: `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(activeShort.title)}`,
        whatsapp: `https://wa.me/?text=${encodeURIComponent(`${activeShort.title} ${url}`)}`,
        email: `mailto:?subject=${encodeURIComponent(activeShort.title)}&body=${encodeURIComponent(url)}`,
      };
      window.open(destinations[channel], '_blank', 'noopener,noreferrer');
    }
    await trackShare(channel);
    setShareOpen(false);
  };

  if (!loading && shorts.length === 0) return null;

  return (
    <>
      <div className="a-rail-label">
        <h3>Shorts</h3>
        <a href="https://youtube.com/@guhso/shorts" target="_blank" rel="noreferrer">YouTube channel →</a>
      </div>
      <div className="a-shorts-rail">
        {loading
          ? Array.from({ length: 5 }, (_, index) => <div className="a-short a-short--loading" key={index} aria-hidden="true" />)
          : shorts.map((short) => (
            <button type="button" className="a-short" key={short.id} onClick={() => { setActiveShort(short); setComments([]); setCommentsOpen(false); setShareOpen(false); }} style={short.thumbnailUrl ? { backgroundImage: `url(${resolveImageUrl(short.thumbnailUrl)})` } : undefined} aria-label={`Play ${short.title}`}>
              <span className="a-short-badge">{short.source === 'youtube' ? 'YouTube' : 'Guhso'}</span>
              <span className="a-short-ttl">{short.title}</span>
              <span className="a-short-dur">{short.durationLabel}</span>
              <span className="a-short-play" aria-hidden="true" />
            </button>
          ))}
      </div>

      {activeShort && (
        <div className="short-player-modal" role="dialog" aria-modal="true" aria-label={`Playing ${activeShort.title}`} onWheel={handleViewerWheel} onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd} onMouseDown={(event) => { if (event.target === event.currentTarget) setActiveShort(null); }}>
          <div className={`short-player-shell${commentsOpen || shareOpen ? ' short-player-shell--panel' : ''}`}>
            <button type="button" className="short-player-close" onClick={() => setActiveShort(null)} aria-label="Close video">×</button>
            <div className="short-player-layout">
              <div className="short-video-column">
                {activeShort.source === 'youtube' && activeShort.embedUrl ? (
                  <iframe key={activeShort.id} src={activeShort.embedUrl} title={activeShort.title} allow="autoplay; encrypted-media; picture-in-picture" allowFullScreen />
                ) : (
                  <video key={activeShort.id} src={activeShort.videoUrl} poster={resolveImageUrl(activeShort.thumbnailUrl) || undefined} controls controlsList="nodownload noremoteplayback" autoPlay loop playsInline preload="metadata" onContextMenu={(event) => event.preventDefault()} />
                )}
                {activeShort.showWatermark !== false && (
                  <img className={`short-watermark short-watermark--${activeShort.watermarkPosition || 'top-left'}`} src="/logo.png" alt="GUHSO" draggable="false" />
                )}
                <div className="short-player-copy"><h4>{activeShort.title}</h4>{activeShort.description && <p>{activeShort.description}</p>}</div>
                {shorts.length > 1 && (
                  <div className="short-nav-controls" aria-label="Browse Shorts">
                    <button type="button" onClick={() => navigateShort(-1)} aria-label="Previous Short">↑</button>
                    <button type="button" onClick={() => navigateShort(1)} aria-label="Next Short">↓</button>
                  </div>
                )}
                <div className="short-action-rail" aria-label="Short actions">
                  <button type="button" className={activeShort.liked ? 'is-active' : ''} onClick={handleLike} disabled={actionBusy} aria-pressed={Boolean(activeShort.liked)}><span><Icon name="like" /></span><b>{countLabel(activeShort.likeCount)}</b><em>Like</em></button>
                  <button type="button" onClick={openComments}><span><Icon name="comment" /></span><b>{countLabel(activeShort.commentCount)}</b><em>Comments</em></button>
                  <button type="button" onClick={openShare}><span><Icon name="share" /></span><b>{countLabel(activeShort.shareCount)}</b><em>Share</em></button>
                </div>
              </div>
              {commentsOpen && (
                <aside className="short-comments-panel" aria-label="Comments">
                  <div className="short-panel-heading"><h5>Comments <span>{countLabel(activeShort.commentCount)}</span></h5><button type="button" onClick={() => setCommentsOpen(false)} aria-label="Close comments">×</button></div>
                  {authLoading ? (
                    <p className="short-comment-auth">Checking your account…</p>
                  ) : isAuthenticated ? (
                    <form onSubmit={submitComment} className="short-comment-form">
                      <p className="short-comment-identity">Commenting as <strong>{user?.name || 'Guhso member'}</strong></p>
                      <textarea value={commentText} onChange={(event) => setCommentText(event.target.value)} placeholder="Add a comment…" maxLength="500" required aria-label="Comment" />
                      <div><small>{commentText.length}/500</small><button type="submit" disabled={commentSaving}>{commentSaving ? 'Posting…' : 'Comment'}</button></div>
                    </form>
                  ) : (
                    <div className="short-comment-auth">
                      <strong>Join the conversation</strong>
                      <p>Sign in with your email to leave a comment.</p>
                      <Link to={`/signin?redirect=${encodeURIComponent(`${window.location.pathname}?short=${activeShort.id}#shorts`)}`}>Sign in to comment</Link>
                    </div>
                  )}
                  <div className="short-comment-list">
                    {commentsLoading && <p className="short-panel-status">Loading comments…</p>}
                    {!commentsLoading && !comments.length && <p className="short-panel-status">No comments yet. Start the conversation.</p>}
                    {comments.map((comment) => <article key={comment.id}><span>{comment.name.slice(0, 1).toUpperCase()}</span><div><strong>{comment.name}</strong><small>{comment.createdLabel}</small><p>{comment.content}</p></div></article>)}
                  </div>
                </aside>
              )}
              {shareOpen && (
                <aside className="short-share-panel" aria-label="Share this Short">
                  <div className="short-panel-heading"><h5>Share</h5><button type="button" onClick={() => setShareOpen(false)} aria-label="Close sharing">×</button></div>
                  <p>Share “{activeShort.title}”</p>
                  <div className="short-share-options">
                    <button type="button" onClick={() => shareTo('copy')}><Icon name="copy" />Copy link</button>
                    <button type="button" onClick={() => shareTo('facebook')}><b>f</b>Facebook</button>
                    <button type="button" onClick={() => shareTo('x')}><b>𝕏</b>X</button>
                    <button type="button" onClick={() => shareTo('whatsapp')}><b>W</b>WhatsApp</button>
                    <button type="button" onClick={() => shareTo('email')}><b>@</b>Email</button>
                  </div>
                </aside>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ShortsRail;
